/**
 * Meta Pixel client wrapper.
 *
 * Scope for the South Africa QA traffic test:
 *   - PageView via loader
 *   - SignUp after first successful magic-link signup
 *   - NicheStartedIntent when a user submits the generation flow
 *   - FreeNicheStarted/Lead after a free landing generation is enqueued
 *   - NicheStarted after a logged-in/paid/regen niche generation is enqueued
 *   - CompleteNiche when a completed niche result renders
 *
 * Purchase is server-side CAPI only so payment webhooks stay the source of
 * truth and browser success-return pages do not double-count revenue.
 */

import { META_EVENTS } from "@/lib/analytics/meta-events";

type MetaPixelParams = Record<string, string | number | boolean | undefined>;
interface MetaPixelOptions {
  eventId?: string;
}
type MetaFbq = {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[];
  loaded?: boolean;
  version?: string;
  push?: MetaFbq;
};

declare global {
  interface Window {
    fbq?: MetaFbq;
    _fbq?: MetaFbq;
  }
}

let initialized = false;
let pageViewSent = false;
let lastPageViewKey: string | null = null;

function readPixelId(): string | null {
  const pixelId = process.env.NEXT_PUBLIC_META_PIXEL_ID?.trim();
  return pixelId || null;
}

function sanitizeParams(
  params?: MetaPixelParams,
): Record<string, string | number | boolean> | undefined {
  if (!params) return undefined;
  const out: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) out[key] = value;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function installFbqStub() {
  if (window.fbq) return;

  const fbq = ((...args: unknown[]) => {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
    } else {
      fbq.queue?.push(args);
    }
  }) as MetaFbq;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.queue = [];

  window.fbq = fbq;
  window._fbq = fbq;
}

function injectScript() {
  if (document.getElementById("meta-pixel-sdk")) return;
  const script = document.createElement("script");
  script.id = "meta-pixel-sdk";
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  const firstScript = document.getElementsByTagName("script")[0];
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }
}

function readCurrentPageKey(): string {
  if (typeof window === "undefined") return "";
  return `${window.location.pathname}${window.location.search}`;
}

export function ensureMetaPixel(): boolean {
  if (typeof window === "undefined") return false;
  const pixelId = readPixelId();
  if (!pixelId) return false;

  installFbqStub();
  injectScript();

  if (!initialized) {
    window.fbq?.("init", pixelId);
    initialized = true;
  }
  if (!pageViewSent) {
    window.fbq?.("track", "PageView");
    pageViewSent = true;
    lastPageViewKey = readCurrentPageKey();
  }
  return true;
}

export function captureMetaPixelPageView(pageKey = readCurrentPageKey()): void {
  if (!ensureMetaPixel()) return;
  if (lastPageViewKey === pageKey) return;
  window.fbq?.("track", "PageView");
  lastPageViewKey = pageKey;
}

export function captureMetaPixelCustom(
  event: string,
  params?: MetaPixelParams,
  options?: MetaPixelOptions,
): void {
  if (!ensureMetaPixel()) return;
  window.fbq?.(
    "trackCustom",
    event,
    sanitizeParams(params),
    options?.eventId ? { eventID: options.eventId } : undefined,
  );
}

export function captureMetaPixelStandard(
  event: string,
  params?: MetaPixelParams,
  options?: MetaPixelOptions,
): void {
  if (!ensureMetaPixel()) return;
  window.fbq?.(
    "track",
    event,
    sanitizeParams(params),
    options?.eventId ? { eventID: options.eventId } : undefined,
  );
}

export function captureMetaPixelCustomOnce(
  event: string,
  dedupKey: string,
  params?: MetaPixelParams,
  options?: MetaPixelOptions,
): boolean {
  if (typeof window === "undefined") return false;
  const storageKey = `meta_pixel_once:${event}:${dedupKey}`;
  try {
    if (localStorage.getItem(storageKey)) return false;
    localStorage.setItem(storageKey, "1");
  } catch {
    // localStorage unavailable — still send the conversion rather than losing it.
  }
  captureMetaPixelCustom(event, params, options);
  return true;
}

export function captureMetaFromAnalyticsEvent(
  name: string,
  params?: MetaPixelParams,
): void {
  if (name === "niche_generate") {
    captureMetaPixelCustom(META_EVENTS.NICHE_STARTED_INTENT, {
      generation_type: "niche",
      has_email: params?.has_email,
    });
    return;
  }

  // Purchase is intentionally not mapped here; see meta-capi.ts.
}
